import { defineNuxtPlugin } from '#app';

import { useVersionInfo } from '~/composables/useVersionInfo';
import { createLogger } from '~/utils/logger';

const logger = createLogger('version-inspector');

/**
 * Version Inspector Plugin
 *
 * Exposes version information globally for debugging purposes.
 *
 * Usage:
 * - Console: `window.showVersion()` - Opens the version panel
 * - Console: `window.hideVersion()` - Closes the version panel
 * - Console: `window.logVersion()` - Logs version info to console
 * - Console: `window.getVersion()` - Returns version info object
 *
 * Triggers:
 * - Keyboard: Press Alt + Shift + V three times quickly to toggle version panel
 * - PostMessage: Send `{ type: 'showVersion' }` to show panel
 * - PostMessage: Send `{ type: 'hideVersion' }` to hide panel
 * - PostMessage: Send `{ type: 'toggleVersion' }` to toggle panel
 *
 * For iOS/Android native apps, trigger via postMessage from native code.
 */
export default defineNuxtPlugin(() => {
    // Only run on client side
    if (import.meta.server) return;

    const { versionInfo, logVersionInfo } = useVersionInfo();

    // State for version panel visibility (will be connected by GlobalWrapper)
    let toggleVersionPanel: (() => void) | null = null;
    let showVersionPanelFn: (() => void) | null = null;
    let hideVersionPanelFn: (() => void) | null = null;

    // Track key presses for quick 'v' key combo
    let keyPressCount = 0;
    let keyPressTimer: ReturnType<typeof setTimeout> | null = null;
    const KEY_COMBO_TIMEOUT = 500; // ms between key presses

    const handleKeyPress = (e: KeyboardEvent) => {
        // Ignore if user is typing in an input
        if (
            e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLTextAreaElement ||
            (e.target instanceof HTMLElement && e.target.isContentEditable)
        ) {
            return;
        }

        // Require Alt + Shift + V to be pressed 3 times
        // Use e.code instead of e.key to avoid issues with Alt producing special characters
        if (e.code === 'KeyV' && e.altKey && e.shiftKey) {
            keyPressCount++;

            if (keyPressTimer) {
                clearTimeout(keyPressTimer);
            }

            if (keyPressCount >= 3) {
                keyPressCount = 0;
                if (toggleVersionPanel) {
                    toggleVersionPanel();
                } else {
                    logVersionInfo();
                }
            } else {
                keyPressTimer = setTimeout(() => {
                    keyPressCount = 0;
                }, KEY_COMBO_TIMEOUT);
            }
        }
    };

    // Handle postMessage from native apps
    const handleMessage = (e: MessageEvent) => {
        // Try to parse if it's a string
        let data = e.data;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                return;
            }
        }

        if (!data || typeof data !== 'object') return;

        switch (data.type) {
            case 'showVersion':
                if (showVersionPanelFn) {
                    showVersionPanelFn();
                } else {
                    logVersionInfo();
                }
                break;
            case 'hideVersion':
                if (hideVersionPanelFn) {
                    hideVersionPanelFn();
                }
                break;
            case 'toggleVersion':
                if (toggleVersionPanel) {
                    toggleVersionPanel();
                } else {
                    logVersionInfo();
                }
                break;
            case 'getVersion':
                // Post back the version info
                window.parent?.postMessage(
                    {
                        type: 'versionInfo',
                        data: versionInfo.value,
                    },
                    '*'
                );
                break;
        }
    };

    // Set up event listeners
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('message', handleMessage);

    // Expose global functions
    window.logVersion = () => {
        return logVersionInfo();
    };

    window.getVersion = () => {
        return versionInfo.value;
    };

    window.showVersion = () => {
        if (showVersionPanelFn) {
            showVersionPanelFn();
        } else {
            logger.warn('Version panel unavailable', {
                event: 'ui.control_unavailable',
                control: 'version_panel',
                fallback: 'logVersion',
            });
            logVersionInfo();
        }
    };

    window.hideVersion = () => {
        if (hideVersionPanelFn) {
            hideVersionPanelFn();
        }
    };

    // Provide a way for components to register the panel controls
    const registerVersionPanel = (controls: {
        toggle: () => void;
        show: () => void;
        hide: () => void;
    }) => {
        toggleVersionPanel = controls.toggle;
        showVersionPanelFn = controls.show;
        hideVersionPanelFn = controls.hide;
    };

    return {
        provide: {
            versionInfo,
            registerVersionPanel,
        },
    };
});

// Type declarations for global window functions
declare global {
    interface Window {
        logVersion: () => ReturnType<typeof useVersionInfo>['versionInfo']['value'];
        getVersion: () => ReturnType<typeof useVersionInfo>['versionInfo']['value'];
        showVersion: () => void;
        hideVersion: () => void;
    }
}
